import React, { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import ComingSoonModal from 'components/ComingSoonModal';
import EducationDrawer from 'components/EducationDrawer';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Tooltip } from 'components/FormFields';
import QuickActions from 'components/QuickActions';
import SeasonPassIcon from 'components/SeasonPassIcon';
import TicketBreakdown from 'components/TicketBreakdown';
import Toaster from 'components/Toaster';
import { AppContext } from 'shared/AppContext';
import { formatTimestamp } from 'shared/formatters';
import AnimatedMetric from '../components/Customers/AnimatedMetric';
import OpenCreditsCard from '../components/OpenCreditsCard';
import GetCustomerBehavioralStats from '../gql/queries/get-customer-behavioral-stats';
import GetCustomerDetailStats from '../gql/queries/get-customer-detail-stats';
import GetCustomerQuickStats from '../gql/queries/get-customer-quick-stats';
import GetCustomerTransaction from '../gql/queries/get-customer-transactions';
import CustomerDetailSkeleton from './CustomerDetailSkeleton';
import {
  Container,
  CustomerHeaderContainer,
  CustomerHeaderLeft,
  CustomerAvatarWrapper,
  CustomerAvatar,
  CustomerInfo,
  CustomerName,
  CustomerMeta,
  CustomerMetaItem,
  CustomerHeaderRight,
  LifetimeValue,
  LifetimeAmount,
  LifetimeLabel,
  DashboardLayout,
  MainContent,
  Sidebar,
  MetricsGrid,
  TransactionCard,
  TransactionHeader,
  TransactionTitle,
  TabsContainer,
  Tab,
  TransactionList,
  TransactionItem,
  TransactionItemHeader,
  TransactionItemLeft,
  TransactionRightSection,
  StatusBadge,
  DateBadge,
  EventName,
  EventSummary,
  TransactionAmount,
  ExpandIcon,
  TransactionDetails,
  DetailGrid,
  DetailColumn,
  DetailLabel,
  DetailValue,
  DetailActions,
  QuickStatsCard,
  QuickStatsTitle,
  QuickStatItem,
  QuickStatIcon,
  QuickStatContent,
  QuickStatLabel,
  QuickStatValue,
  QuickStatSubtext,
  PatternsCard,
  PatternsTitle,
  FilterPills,
  PatternHighlight,
  PatternLabel,
  PatternValue,
  PatternSection,
  PatternSectionTitle,
  LoadingContainer,
  Pagination,
  PageEllipsis,
  SegmentBadge,
  DrawerSection,
  HelpButton,
  PatternsTitleContainer,
  IconWrapper,
  HabitGrid,
  HabitItem,
  HabitIcon,
  HabitContent,
  HabitTitle,
  HabitDescription,
  CountBadge,
} from './StyledComponents';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

const CustomerDetail = ({
  data,
  statsData,
  behavioralData,
  quickStatsData,
  loading,
  error,
  match,
}) => {
  const history = useHistory();
  const location = useLocation();
  const userId = match?.params?.id;

  // Get capabilities from AppContext
  const {
    state: { capabilities },
  } = useContext(AppContext);

  // Parse page and year from URL
  const searchParams = new URLSearchParams(location.search);
  const currentYear = String(new Date().getFullYear());
  const currentPageFromUrl = parseInt(searchParams.get('page') || '1');
  const yearFromUrl = searchParams.get('year') || 'All';

  const [activeTab, setActiveTab] = useState(yearFromUrl);
  const [expandedItems, setExpandedItems] = useState({});
  const [currentPage, setCurrentPage] = useState(currentPageFromUrl);
  const [toasterData, setToasterData] = useState(null);
  const [showBehavioralEducation, setShowBehavioralEducation] = useState(false);

  // Sync activeTab with URL when location changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    const newYear = newSearchParams.get('year') || 'All';
    const newPage = parseInt(newSearchParams.get('page') || '1');
    setActiveTab(newYear);
    setCurrentPage(newPage);
  }, [location.search]);

  // Default to current year after data loads if customer has transactions this year
  // and no explicit year param is in the URL
  useEffect(() => {
    const yearCounts = data?.getCustomerTransactions?.yearCounts || [];
    if (
      !searchParams.get('year') &&
      activeTab === 'All' &&
      yearCounts.some((y) => String(y.year) === currentYear)
    ) {
      setActiveTab(currentYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.getCustomerTransactions?.yearCounts]);

  // Check for success message from Issue Credit on mount
  useEffect(() => {
    const creditSuccessMessage = sessionStorage.getItem('creditSuccessMessage');
    if (creditSuccessMessage) {
      setToasterData({
        title: 'Credit Issued!',
        message: creditSuccessMessage,
        type: 'success',
      });
      // Clear the message from sessionStorage
      sessionStorage.removeItem('creditSuccessMessage');
    }
  }, []);

  // Note: The GraphQL HOC already watches location changes and refetches automatically
  // No need to manually call data.refetch() here

  // Determine the route prefix (admin-track or admin-employee)
  const routePrefix = location.pathname.includes('admin-track')
    ? '/admin-track'
    : '/admin-employee';

  // Check if user is Admin (matches TransactionDetailNew logic)
  const isAdmin = location.pathname.includes('/admin/');

  // Get customer data from API response
  const transactionData = data?.getCustomerTransactions?.results || [];
  const totalCount = data?.getCustomerTransactions?.count || 0;
  const customer = data?.getCustomerTransactions?.customer || {};
  const itemsPerPage = 15;

  // Calculate total pages based on count from API
  // Handle edge cases where API count might be off or create empty pages
  let totalPages;
  if (totalCount === 0 || transactionData.length === 0) {
    // No transactions or no results on current page
    totalPages = currentPage > 1 ? currentPage - 1 : 0;
  } else if (transactionData.length < itemsPerPage) {
    // Current page has partial results, so this is the last page
    totalPages = currentPage;
  } else {
    // Current page is full (has exactly itemsPerPage items)
    // Calculate pages based on total count
    const calculatedPages = Math.ceil(totalCount / itemsPerPage);

    // Check for off-by-one errors in API count:
    // If on page 1 with full results and count suggests exactly 1 more item
    // (which would create a page 2 with just 1 item), this is likely incorrect
    if (
      currentPage === 1 &&
      transactionData.length === itemsPerPage &&
      totalCount > itemsPerPage &&
      totalCount <= itemsPerPage + 1
    ) {
      // Likely off-by-one error, only show 1 page
      totalPages = 1;
    } else {
      totalPages = calculatedPages;
    }
  }

  // Loading state for any query OR if customer data isn't available yet
  if (
    loading ||
    statsData?.loading ||
    behavioralData?.loading ||
    quickStatsData?.loading ||
    !customer ||
    !customer.id
  ) {
    return <CustomerDetailSkeleton />;
  }

  // Error state
  if (
    error ||
    statsData?.error ||
    behavioralData?.error ||
    quickStatsData?.error
  ) {
    return (
      <EnhancedThemeProvider>
        <LoadingContainer>
          <div>
            Error loading customer details:{' '}
            {error?.message || statsData?.error?.message}
          </div>
        </LoadingContainer>
      </EnhancedThemeProvider>
    );
  }

  // Remove the early return - we'll handle empty states in the UI instead
  const hasTransactions = transactionData && transactionData.length > 0;

  // Handle page change
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setExpandedItems({}); // Reset expanded items when changing page

    // Update URL with page parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page.toString());
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Helper function to parse currency strings
  const parseCurrency = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Remove $ and commas, then parse
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  };

  // Format phone number to (323) 842-1428 format
  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phone;
  };

  // Get customer info from the API customer field
  const customerName =
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
    'Unknown Customer';
  const customerEmail = customer.email || 'No email';
  const customerPhone = formatPhone(customer.cellphone) || 'No phone';
  const customerSince = customer.customer_since || null;
  const customerZipcode = customer.zipcode || null;
  const customerCity = customer.city || null;
  const customerState = customer.state || null;

  // Calculate metrics from transactions since they're not in the customer field
  // Use stats API value if available, otherwise use count from transactions
  const totalOrders =
    statsData?.getCustomerDetailStats?.totalOrders?.value || totalCount || 0;
  // Calculate total revenue from all transactions on current page (approximation)
  const pageRevenue = hasTransactions
    ? transactionData.reduce((sum, t) => sum + parseCurrency(t.total), 0)
    : 0;
  const avgPageOrderValue =
    hasTransactions && transactionData.length > 0
      ? pageRevenue / transactionData.length
      : 0;
  const totalRevenue = avgPageOrderValue * totalOrders; // Estimate based on average
  const avgOrderValue =
    totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : 0;

  // Format transactions for display
  const formattedTransactions = hasTransactions
    ? transactionData.map((t) => {
        const purchaseDate = new Date(Number(t.purchase_date));
        // Collect all tickets from all users (for Pit Pay)
        const tickets = t.users
          ? t.users.flatMap((user) => user.tickets || [])
          : [];
        const ticketSummary =
          tickets
            .map((ticket) => ticket.name)
            .filter(Boolean)
            .join(', ') || `No ${getTicketText(2)} details`;

        // Check if this is a non-event purchase (annual/membership)
        const isNonEventPurchase = !t.event;
        let eventName,
          trackName,
          displayMonth,
          displayDay,
          isMultiDay = false;

        if (isNonEventPurchase) {
          // Determine the type of non-event purchase
          const ticketTypes = tickets
            .map((ticket) => ticket.type)
            .filter(Boolean);
          const hasAnnual = ticketTypes.some((type) => type === 'annual');
          const hasMembership = ticketTypes.some(
            (type) => type === 'membership'
          );

          if (hasAnnual && !hasMembership) {
            eventName = 'Annual Pass Purchase';
          } else if (hasMembership && !hasAnnual) {
            eventName = 'Membership Purchase';
          } else if (hasAnnual && hasMembership) {
            eventName = 'Pass/Membership Purchase';
          } else {
            // Fallback if type is not available
            eventName = 'Pass Purchase';
          }

          // For non-event purchases, use year for date display
          trackName = ''; // Don't show year again since it's in the date badge
          // For annual/membership, show the year in the date box
          displayMonth = '';
          displayDay = purchaseDate.getFullYear().toString();
        } else {
          // Regular event purchase - try to use event date
          eventName = t.event.name || 'Unknown Event';
          trackName = t.track?.name || 'Unknown Track';

          // Check if event has dates and if it's multi-day
          if (t.event.start_date && t.event.end_date) {
            // The dates might be ISO strings or timestamps
            let startMs, endMs;

            // Check if it's an ISO date string (contains '-' or 'T')
            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              // It's a timestamp - parse and check length
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              // If timestamp has 10 or fewer digits, it's in seconds
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }

            if (
              typeof t.event.end_date === 'string' &&
              (t.event.end_date.includes('-') || t.event.end_date.includes('T'))
            ) {
              endMs = new Date(t.event.end_date).getTime();
            } else {
              const endTimestamp =
                typeof t.event.end_date === 'string'
                  ? parseInt(t.event.end_date)
                  : t.event.end_date;
              endMs =
                endTimestamp.toString().length <= 10
                  ? endTimestamp * 1000
                  : endTimestamp;
            }

            // Check if it's a multi-day event
            isMultiDay = startMs !== endMs;

            if (isMultiDay) {
              // For multi-day events, show date range
              const startMonth = formatTimestamp(startMs, 'MMM', true);
              const startDay = formatTimestamp(startMs, 'DD', true);
              const endMonth = formatTimestamp(endMs, 'MMM', true);
              const endDay = formatTimestamp(endMs, 'DD', true);

              // If same month, show as "MMM DD-DD", otherwise "MMM DD - MMM DD"
              if (startMonth === endMonth) {
                displayMonth = startMonth;
                displayDay = `${startDay}-${endDay}`;
              } else {
                displayMonth = `${startMonth}-${endMonth}`;
                displayDay = `${startDay}-${endDay}`;
              }
            } else {
              // Single day event
              displayMonth = formatTimestamp(startMs, 'MMM', true);
              displayDay = formatTimestamp(startMs, 'DD', true);
            }
          } else if (t.event.start_date) {
            // Only start_date available
            let startMs;
            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }
            displayMonth = formatTimestamp(startMs, 'MMM', true);
            displayDay = formatTimestamp(startMs, 'DD', true);
          } else {
            // Fallback to purchase date if no event date
            displayMonth = formatTimestamp(
              Number(t.purchase_date),
              'MMM',
              true
            );
            displayDay = formatTimestamp(Number(t.purchase_date), 'DD', true);
          }
        }

        return {
          id: t.id,
          year: purchaseDate.getFullYear().toString(),
          month: displayMonth,
          day: displayDay,
          eventName: eventName,
          trackName: trackName,
          ticketInfo: ticketSummary,
          ticketCount: tickets.length,
          total: parseCurrency(t.total),
          profit: parseCurrency(t.profit),
          fee: parseCurrency(t.fee),
          discount: parseCurrency(t.discount),
          refund: parseCurrency(t.refund),
          status: t.status || 'completed',
          orderSource: t.order_source || 'App',
          tickets: tickets,
          isNonEventPurchase: isNonEventPurchase,
          isMultiDay: isMultiDay,
          eventStatus: t.event?.status || null,
        };
      })
    : [];

  // Get yearCounts from API to determine which years have data
  const yearCountsData = data?.getCustomerTransactions?.yearCounts || [];

  // No need to filter client-side anymore - API handles year filtering
  const filteredTransactions = formattedTransactions;

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Remove the getCustomerSegment function since we don't want to show fake data

  const fromTransaction = location.state?.from;

  return (
    <EnhancedThemeProvider>
      <Container>
        {fromTransaction && (
          <div
            style={{
              marginBottom: '16px',
            }}
          >
            <button
              onClick={() => history.push(fromTransaction)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#fa4616',
                fontSize: '14px',
                fontWeight: '500',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              ← Back to Transaction
            </button>
          </div>
        )}
        {/* Customer Header */}
        <CustomerHeaderContainer>
          <CustomerHeaderLeft>
            <CustomerAvatarWrapper>
              <CustomerAvatar>
                {customer.first_name && customer.last_name
                  ? `${customer.first_name[0]}${customer.last_name[0]}`.toUpperCase()
                  : customerName
                      .split(' ')
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
              </CustomerAvatar>
              {customer.has_season_pass && <SeasonPassIcon />}
            </CustomerAvatarWrapper>
            <CustomerInfo>
              <CustomerName>{customerName}</CustomerName>
              <CustomerMeta>
                {customerPhone && (
                  <CustomerMetaItem>
                    <a href={`tel:${customer.cellphone}`}>{customerPhone}</a>
                  </CustomerMetaItem>
                )}
                <CustomerMetaItem>
                  <a href={`mailto:${customerEmail}`}>{customerEmail}</a>
                </CustomerMetaItem>
              </CustomerMeta>
            </CustomerInfo>
          </CustomerHeaderLeft>
          <CustomerHeaderRight>
            <div>
              <LifetimeValue>
                <LifetimeAmount>
                  {data?.getCustomerTransactions?.customer?.lifetime_value ||
                    '$0.00'}
                </LifetimeAmount>
                <LifetimeLabel>Lifetime Value</LifetimeLabel>
              </LifetimeValue>
              {customerSince && (
                <div
                  style={{
                    marginTop: '12px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '14px',
                  }}
                >
                  Customer since {customerSince}
                </div>
              )}
            </div>
          </CustomerHeaderRight>
        </CustomerHeaderContainer>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Metrics Grid */}
            <MetricsGrid>
              <AnimatedMetric
                value={
                  statsData?.getCustomerDetailStats?.avgOrderValue?.value ||
                  parseFloat(avgOrderValue)
                }
                label="Avg Order Value"
                isCurrency={true}
                changeText={
                  statsData?.getCustomerDetailStats?.avgOrderValue
                    ?.changeText || '+0%'
                }
                isPositive={
                  statsData?.getCustomerDetailStats?.avgOrderValue
                    ?.isPositive || false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={statsData?.getCustomerDetailStats?.spentYTD?.value || 0}
                label="Spent YTD"
                changeText={
                  statsData?.getCustomerDetailStats?.spentYTD?.changeText ||
                  '$0'
                }
                isPositive={
                  statsData?.getCustomerDetailStats?.spentYTD?.isPositive ||
                  false
                }
                isCurrency={true}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4" />
                    <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2z" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={
                  statsData?.getCustomerDetailStats?.totalOrders?.value ||
                  totalOrders
                }
                label="Total Orders"
                changeText={
                  statsData?.getCustomerDetailStats?.totalOrders?.changeText ||
                  '+0'
                }
                isPositive={
                  statsData?.getCustomerDetailStats?.totalOrders?.isPositive ||
                  false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5M3.14 5l1.25 5h8.22l1.25-5zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0m9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={
                  statsData?.getCustomerDetailStats?.eventsAttendedYTD?.value ||
                  0
                }
                label="Events Attended YTD"
                isPercentage={false}
                changeText={
                  statsData?.getCustomerDetailStats?.eventsAttendedYTD
                    ?.changeText || ''
                }
                isPositive={
                  statsData?.getCustomerDetailStats?.eventsAttendedYTD
                    ?.isPositive || false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                  </svg>
                }
              />
            </MetricsGrid>

            {/* Transaction History */}
            <TransactionCard>
              <TransactionHeader>
                <TransactionTitle>Transaction History</TransactionTitle>
              </TransactionHeader>

              {hasTransactions && (
                <TabsContainer>
                  {/* All tab - always shown with total count */}
                  <Tab
                    active={activeTab === 'All'}
                    onClick={() => {
                      setActiveTab('All');
                      setCurrentPage(1);
                      // Remove year parameter for "All"
                      const searchParams = new URLSearchParams(location.search);
                      searchParams.delete('year');
                      searchParams.set('page', '1');
                      history.push({
                        pathname: location.pathname,
                        search: searchParams.toString(),
                      });
                    }}
                  >
                    All ({totalCount})
                  </Tab>

                  {/* Year tabs from yearCounts data */}
                  {yearCountsData
                    .sort((a, b) => {
                      // Convert to strings if they're numbers
                      const yearA = String(a.year);
                      const yearB = String(b.year);
                      return yearB.localeCompare(yearA); // Sort years descending
                    })
                    .map(({ year, count }) => (
                      <Tab
                        key={year}
                        active={String(activeTab) === String(year)}
                        onClick={() => {
                          setActiveTab(String(year));
                          setCurrentPage(1);
                          // Update URL with year parameter
                          const searchParams = new URLSearchParams(
                            location.search
                          );
                          searchParams.set('year', String(year));
                          searchParams.set('page', '1');
                          history.push({
                            pathname: location.pathname,
                            search: searchParams.toString(),
                          });
                        }}
                      >
                        {year} ({count})
                      </Tab>
                    ))}
                </TabsContainer>
              )}

              <TransactionList>
                {hasTransactions && filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => (
                    <TransactionItem key={transaction.id}>
                      <TransactionItemHeader
                        onClick={() => toggleExpand(transaction.id)}
                      >
                        <TransactionItemLeft>
                          <DateBadge>
                            <div className="date-day">{transaction.day}</div>
                            {transaction.month && (
                              <div className="date-month">
                                {transaction.month}
                              </div>
                            )}
                          </DateBadge>
                          <div>
                            <EventName>{transaction.eventName}</EventName>
                            <EventSummary>
                              <span>{transaction.trackName}</span>
                              <span>
                                {`${transaction.ticketCount} ${getTicketText(
                                  transaction.ticketCount
                                )}`}
                              </span>
                            </EventSummary>
                          </div>
                        </TransactionItemLeft>
                        <TransactionRightSection>
                          {transaction.eventStatus &&
                            (transaction.eventStatus === 'cancelled' ||
                              transaction.eventStatus === 'postponed') && (
                              <StatusBadge status={transaction.eventStatus}>
                                {transaction.eventStatus === 'cancelled'
                                  ? 'Cancelled'
                                  : 'Postponed'}
                              </StatusBadge>
                            )}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '20px',
                            }}
                          >
                            <TransactionAmount>
                              $
                              {transaction.profit.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TransactionAmount>
                            <ExpandIcon
                              expanded={expandedItems[transaction.id]}
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                            </ExpandIcon>
                          </div>
                        </TransactionRightSection>
                      </TransactionItemHeader>

                      {expandedItems[transaction.id] && (
                        <TransactionDetails>
                          <DetailGrid>
                            <DetailColumn>
                              <div>
                                <DetailLabel>
                                  {getTicketText(2).toUpperCase()} PURCHASED
                                </DetailLabel>
                                {(() => {
                                  const rawTransaction = transactionData.find(
                                    (t) => t.id === transaction.id
                                  );
                                  // Collect all tickets from all users (for Pit Pay)
                                  const tickets = rawTransaction?.users
                                    ? rawTransaction.users.flatMap(
                                        (user) => user.tickets || []
                                      )
                                    : [];

                                  return (
                                    <TicketBreakdown
                                      tickets={tickets}
                                      emptyText={
                                        transaction.ticketInfo ||
                                        `No ${getTicketText(2)} details`
                                      }
                                    />
                                  );
                                })()}
                              </div>
                            </DetailColumn>
                            <DetailColumn alignRight>
                              <div>
                                <DetailLabel>PURCHASE DATE</DetailLabel>
                                <DetailValue>
                                  {formatTimestamp(
                                    Number(
                                      transactionData.find(
                                        (t) => t.id === transaction.id
                                      ).purchase_date
                                    ),
                                    'MMM D, YYYY h:mm A',
                                    true
                                  )}
                                </DetailValue>
                              </div>
                              <div>
                                <DetailLabel>PAYMENT</DetailLabel>
                                <DetailValue>
                                  {(() => {
                                    const rawTransaction = transactionData.find(
                                      (t) => t.id === transaction.id
                                    );
                                    const discount = parseCurrency(
                                      rawTransaction?.discount
                                    );
                                    const credit = parseCurrency(
                                      rawTransaction?.credit
                                    );
                                    const paymentMethod =
                                      rawTransaction?.payment_method;

                                    let paymentText = 'N/A';

                                    // Check if any credit was used (credit > 0)
                                    if (credit > 0) {
                                      paymentText = 'CREDIT';
                                    }
                                    // Check if discount was applied (discount > 0 means discount was used)
                                    else if (discount > 0) {
                                      paymentText = 'DISCOUNT';
                                    }
                                    // Otherwise show payment method
                                    else if (paymentMethod) {
                                      paymentText = paymentMethod.toUpperCase();
                                    }

                                    return <Badge>{paymentText}</Badge>;
                                  })()}
                                </DetailValue>
                              </div>
                              <div>
                                <DetailLabel>SOURCE</DetailLabel>
                                <DetailValue>
                                  <Badge>
                                    {transaction.orderSource?.toUpperCase()}
                                  </Badge>
                                </DetailValue>
                              </div>
                            </DetailColumn>
                          </DetailGrid>
                          <DetailActions>
                            {(() => {
                              // Helper functions to check ticket status for this transaction
                              const rawTransaction = transactionData.find(
                                (t) => t.id === transaction.id
                              );

                              const areAllTicketsRefunded = () => {
                                if (!rawTransaction?.users) return false;
                                let totalTickets = 0;
                                let refundedTickets = 0;
                                rawTransaction.users.forEach((user) => {
                                  if (user.tickets) {
                                    user.tickets.forEach((ticket) => {
                                      totalTickets++;
                                      if (ticket.refunded) {
                                        refundedTickets++;
                                      }
                                    });
                                  }
                                });
                                return (
                                  totalTickets > 0 &&
                                  totalTickets === refundedTickets
                                );
                              };

                              const areAnyTicketsCredited = () => {
                                if (!rawTransaction?.users) return false;
                                return rawTransaction.users.some((user) =>
                                  user.tickets?.some(
                                    (ticket) => ticket.credited
                                  )
                                );
                              };

                              const shouldShowRefund =
                                isAdmin ||
                                (capabilities && capabilities?.issue_refunds);
                              const shouldShowCredit =
                                isAdmin ||
                                (capabilities && capabilities?.issue_credits);

                              return (
                                <>
                                  {shouldShowRefund && (
                                    <ButtonV2
                                      variant="pill"
                                      onClick={() => {
                                        // Navigate to Transaction Refund page with transaction ID and return path
                                        const returnPath = `${routePrefix}/users/transactions/${userId}`;
                                        history.replace(
                                          `${routePrefix}/transactions/${
                                            transaction.id
                                          }/refund?customerId=${userId}&returnTo=${encodeURIComponent(
                                            returnPath
                                          )}`
                                        );
                                      }}
                                      disabled={
                                        areAllTicketsRefunded() &&
                                        !areAnyTicketsCredited()
                                      }
                                      title={
                                        areAllTicketsRefunded() &&
                                        !areAnyTicketsCredited()
                                          ? 'All tickets have already been refunded'
                                          : 'Process refund for this transaction'
                                      }
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                          />
                                        </svg>
                                      }
                                    >
                                      Refund
                                    </ButtonV2>
                                  )}
                                  {shouldShowCredit && (
                                    <ButtonV2
                                      variant="pill"
                                      onClick={() => {
                                        // Navigate to Transaction Credit page with transaction ID and return path
                                        const returnPath = `${routePrefix}/users/transactions/${userId}`;
                                        history.replace(
                                          `${routePrefix}/transactions/${
                                            transaction.id
                                          }/credit?customerId=${userId}&returnTo=${encodeURIComponent(
                                            returnPath
                                          )}`
                                        );
                                      }}
                                      disabled={areAllTicketsRefunded()}
                                      title={
                                        areAllTicketsRefunded()
                                          ? 'Cannot issue credit - all tickets have been refunded'
                                          : 'Issue credit for this transaction'
                                      }
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                        </svg>
                                      }
                                    >
                                      Credit
                                    </ButtonV2>
                                  )}
                                  <ButtonV2
                                    variant="pill"
                                    active
                                    onClick={() => {
                                      history.push(
                                        `${routePrefix}/transactions/detail/${transaction.id}`
                                      );
                                    }}
                                    icon={
                                      <svg
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                                        <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                                      </svg>
                                    }
                                  >
                                    View
                                  </ButtonV2>
                                </>
                              );
                            })()}
                          </DetailActions>
                        </TransactionDetails>
                      )}
                    </TransactionItem>
                  ))
                ) : (
                  <div
                    style={{
                      padding: '48px 24px',
                      textAlign: 'center',
                      color: '#9CA3AF',
                      fontSize: '14px',
                      lineHeight: '1.5',
                    }}
                  >
                    <svg
                      width="48"
                      height="48"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{
                        marginBottom: '16px',
                        opacity: '0.5',
                        width: '48px',
                        height: '48px',
                      }}
                    >
                      <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5M3.14 5l1.25 5h8.22l1.25-5zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0m9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0" />
                    </svg>
                    <div style={{ fontWeight: '500', marginBottom: '8px' }}>
                      No transactions found
                    </div>
                    <div style={{ color: '#6B7280', fontSize: '13px' }}>
                      This customer hasn't made any purchases yet.
                    </div>
                  </div>
                )}
              </TransactionList>

              {/* Pagination */}
              {hasTransactions && totalPages > 1 && (
                <Pagination>
                  <ButtonV2
                    variant="icon"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    icon={
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                        />
                      </svg>
                    }
                  />

                  {getPageNumbers().map((page, index) =>
                    page === '...' ? (
                      <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                    ) : (
                      <ButtonV2
                        variant="icon"
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </ButtonV2>
                    )
                  )}

                  <ButtonV2
                    variant="icon"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    icon={
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                        />
                      </svg>
                    }
                  />
                </Pagination>
              )}
            </TransactionCard>
          </MainContent>

          <Sidebar>
            {/* Quick Actions - Moved to top */}
            <div style={{ marginBottom: '30px' }}>
              <QuickActions
                actions={[
                  {
                    label: 'Issue Credit',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                      </svg>
                    ),
                    onClick: () => {
                      // Navigate to Issue Credit page with customer ID from URL and return path
                      const returnPath = `${routePrefix}/users/transactions/${userId}`;
                      history.push(
                        `${routePrefix}/customers/issue-credit/${userId}?returnTo=${encodeURIComponent(
                          returnPath
                        )}`
                      );
                    },
                  },
                  {
                    component: (
                      <ComingSoonModal
                        title="Customer Messaging"
                        description="We're building a powerful messaging system to help you connect with your customers."
                        features={[
                          'Message an individual customer directly',
                          'View history of all past conversations',
                          'Get notifications and inbox for customer messages',
                        ]}
                      >
                        <ButtonV2
                          elevated
                          icon={
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                            </svg>
                          }
                        >
                          Message Customer
                        </ButtonV2>
                      </ComingSoonModal>
                    ),
                  },
                ]}
              />
            </div>

            {/* Quick Stats */}
            <QuickStatsCard>
              <QuickStatsTitle>Quick Stats</QuickStatsTitle>
              <QuickStatItem>
                <QuickStatIcon>
                  {/* Trophy icon for Segment */}
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935z" />
                  </svg>
                </QuickStatIcon>
                <QuickStatContent>
                  <QuickStatLabel>Segment</QuickStatLabel>
                  {quickStatsData?.getCustomerQuickStats?.segmentDisplayName ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <SegmentBadge
                        segment={quickStatsData.getCustomerQuickStats.segment}
                      >
                        {quickStatsData.getCustomerQuickStats.segmentDisplayName.toUpperCase()}
                      </SegmentBadge>
                      {quickStatsData.getCustomerQuickStats.segmentDirection &&
                        (() => {
                          const getTrendIndicator = (trend) => {
                            switch (trend) {
                              case 'up':
                                return {
                                  icon: (
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#10B981"
                                      strokeWidth="3"
                                      width="19"
                                      height="19"
                                    >
                                      <path d="M12 19V5M5 12l7-7 7 7" />
                                    </svg>
                                  ),
                                  label: 'Trending up to a higher segment',
                                };
                              case 'down':
                                return {
                                  icon: (
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#EF4444"
                                      strokeWidth="3"
                                      width="19"
                                      height="19"
                                    >
                                      <path d="M12 5v14M19 12l-7 7-7-7" />
                                    </svg>
                                  ),
                                  label: 'Trending down to a lower segment',
                                };
                              case 'flat':
                              default:
                                return {
                                  icon: (
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#6B7280"
                                      strokeWidth="3"
                                      width="19"
                                      height="19"
                                    >
                                      <path d="M5 12h14" />
                                    </svg>
                                  ),
                                  label: 'No segment change',
                                };
                            }
                          };

                          const indicator = getTrendIndicator(
                            quickStatsData.getCustomerQuickStats
                              .segmentDirection
                          );
                          return (
                            <Tooltip text={indicator.label}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'help',
                                }}
                              >
                                {indicator.icon}
                              </span>
                            </Tooltip>
                          );
                        })()}
                    </div>
                  ) : (
                    <QuickStatValue style={{ color: '#6B7280' }}>
                      —
                    </QuickStatValue>
                  )}
                </QuickStatContent>
              </QuickStatItem>
              <QuickStatItem>
                <QuickStatIcon>
                  {/* Calendar icon for Events */}
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                  </svg>
                </QuickStatIcon>
                <QuickStatContent>
                  <QuickStatLabel>Events Attended Lifetime</QuickStatLabel>
                  <QuickStatValue>
                    {quickStatsData?.getCustomerQuickStats?.eventsAttended ??
                      totalOrders}
                  </QuickStatValue>
                </QuickStatContent>
              </QuickStatItem>
              <QuickStatItem>
                <QuickStatIcon>
                  {/* Map pin icon for Location */}
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                  </svg>
                </QuickStatIcon>
                <QuickStatContent>
                  <QuickStatLabel>Location</QuickStatLabel>
                  <QuickStatValue>
                    {quickStatsData?.getCustomerQuickStats?.location?.zipcode ||
                      customerZipcode ||
                      'No location'}
                  </QuickStatValue>
                  {(() => {
                    const city =
                      quickStatsData?.getCustomerQuickStats?.location?.city ||
                      customerCity;
                    const state =
                      quickStatsData?.getCustomerQuickStats?.location?.state ||
                      customerState;
                    if (city || state) {
                      return (
                        <QuickStatSubtext>
                          {city}
                          {city && state && ', '}
                          {state}
                        </QuickStatSubtext>
                      );
                    }
                    return null;
                  })()}
                </QuickStatContent>
              </QuickStatItem>
              <QuickStatItem>
                <QuickStatIcon>
                  {/* Clock icon for Visit Frequency */}
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                  </svg>
                </QuickStatIcon>
                <QuickStatContent>
                  <QuickStatLabel>Visit Frequency YTD</QuickStatLabel>
                  {quickStatsData?.getCustomerQuickStats
                    ?.averageDaysBetweenVisits ? (
                    <>
                      <QuickStatValue>
                        {`${quickStatsData.getCustomerQuickStats.averageDaysBetweenVisits.toFixed(
                          1
                        )} days`}
                      </QuickStatValue>
                      <QuickStatSubtext>Between visits</QuickStatSubtext>
                    </>
                  ) : (
                    <QuickStatValue style={{ color: '#6B7280' }}>
                      —
                    </QuickStatValue>
                  )}
                </QuickStatContent>
              </QuickStatItem>
              <QuickStatItem>
                <QuickStatIcon>
                  {/* Ticket icon for Last Event */}
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5V6a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 11.5V10a.5.5 0 0 1 .5-.5 1.5 1.5 0 1 0 0-3A.5.5 0 0 1 0 6V4.5ZM1.5 4a.5.5 0 0 0-.5.5v1.05a2.5 2.5 0 0 1 0 4.9v1.05a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-1.05a2.5 2.5 0 0 1 0-4.9V4.5a.5.5 0 0 0-.5-.5h-13Z" />
                  </svg>
                </QuickStatIcon>
                <QuickStatContent>
                  <QuickStatLabel>Last Event Attended</QuickStatLabel>
                  {quickStatsData?.getCustomerQuickStats?.lastEventAttended
                    ?.name ? (
                    <>
                      <QuickStatValue>
                        {
                          quickStatsData.getCustomerQuickStats.lastEventAttended
                            .date
                        }
                      </QuickStatValue>
                      <QuickStatSubtext>
                        {
                          quickStatsData.getCustomerQuickStats.lastEventAttended
                            .name
                        }
                      </QuickStatSubtext>
                    </>
                  ) : (
                    <QuickStatValue style={{ color: '#6B7280' }}>
                      —
                    </QuickStatValue>
                  )}
                </QuickStatContent>
              </QuickStatItem>
            </QuickStatsCard>

            {/* Open Credits Card */}
            <OpenCreditsCard userId={userId} routePrefix={routePrefix} />

            {/* Behavioral Habits */}
            <PatternsCard>
              <PatternsTitleContainer>
                <PatternsTitle>Behavioral Habits</PatternsTitle>
                <HelpButton
                  onClick={() => setShowBehavioralEducation(true)}
                  aria-label="Learn about behavioral habits"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.496 6.033h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286a.237.237 0 0 0 .241.247m2.325 6.443c.61 0 1.029-.394 1.029-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94 0 .533.425.927 1.01.927z" />
                  </svg>
                </HelpButton>
              </PatternsTitleContainer>

              {/* Purchase Patterns */}
              {behavioralData?.getCustomerBehavioralStats?.purchasePatterns
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Purchase Patterns</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getCustomerBehavioralStats.purchasePatterns.map(
                      (pattern, index) => (
                        <Badge key={index} shape="pill" count={pattern.count}>
                          {pattern.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Purchase Timing */}
              {behavioralData?.getCustomerBehavioralStats
                ?.purchaseTimingPatterns?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>
                    Purchase Timing Patterns
                  </PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getCustomerBehavioralStats.purchaseTimingPatterns.map(
                      (timing, index) => (
                        <Badge key={index} shape="pill" count={timing.count}>
                          {timing.label.charAt(0).toUpperCase() +
                            timing.label.slice(1)}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Event Preferences */}
              {behavioralData?.getCustomerBehavioralStats?.eventPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Event Preferences</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getCustomerBehavioralStats.eventPreferences.map(
                      (event, index) => (
                        <Badge key={index} shape="pill" count={event.count}>
                          {event.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Division Preferences */}
              {behavioralData?.getCustomerBehavioralStats?.divisionPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>
                    Division Preferences
                  </PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getCustomerBehavioralStats.divisionPreferences.map(
                      (division, index) => (
                        <Badge key={index} shape="pill" count={division.count}>
                          {division.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Series Preferences */}
              {behavioralData?.getCustomerBehavioralStats?.seriesPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Series Preferences</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getCustomerBehavioralStats.seriesPreferences.map(
                      (series, index) => (
                        <Badge key={index} shape="pill" count={series.count}>
                          {series.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Average Group Size */}
              {behavioralData?.getCustomerBehavioralStats?.averageGroupSize &&
              hasTransactions ? (
                <PatternHighlight>
                  <PatternLabel>Average group size</PatternLabel>
                  <PatternValue>
                    {behavioralData.getCustomerBehavioralStats.averageGroupSize}{' '}
                    {getTicketText(
                      behavioralData.getCustomerBehavioralStats.averageGroupSize
                    )}
                  </PatternValue>
                </PatternHighlight>
              ) : (
                <PatternHighlight>
                  <PatternLabel>Average group size</PatternLabel>
                  <PatternValue style={{ color: '#6B7280' }}>—</PatternValue>
                </PatternHighlight>
              )}
            </PatternsCard>
          </Sidebar>
        </DashboardLayout>
      </Container>

      {/* Success Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={6000}
        />
      )}

      {/* Behavioral Education Drawer */}
      <EducationDrawer
        title="Understanding Behavioral Habits"
        isOpen={showBehavioralEducation}
        onClose={() => setShowBehavioralEducation(false)}
        showHelpButton={false}
      >
        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </IconWrapper>
            Understanding the Numbers
          </h3>
          <p>
            Each behavioral pattern shows a <CountBadge>count</CountBadge>{' '}
            representing how many times this customer has exhibited that
            specific behavior. Higher counts indicate stronger preferences.
          </p>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </IconWrapper>
            Purchase Timing Patterns
          </h3>
          <p>When do they buy {getTicketText(2)}?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Weeks Before</HabitTitle>
                <HabitDescription>
                  Plans ahead and secures {getTicketText(2)} early
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Days Before</HabitTitle>
                <HabitDescription>
                  Buys {getTicketText(2)} within the week of event
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Same Day</HabitTitle>
                <HabitDescription>
                  Last-minute purchaser, decides day-of
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V9z" />
                <path d="M13 5L13 19" />
              </svg>
            </IconWrapper>
            Purchase Patterns
          </h3>
          <p>What do they typically buy?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>
                  Family{' '}
                  {getTicketText(2).charAt(0).toUpperCase() +
                    getTicketText(2).slice(1)}
                </HabitTitle>
                <HabitDescription>
                  Buys family packages and group deals
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M4 18v3h3v-3h10v3h3v-6H4v3zm15-8h3v3h-3v-3zM2 10h3v3H2v-3zm15 3H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Reserved Seating</HabitTitle>
                <HabitDescription>
                  Prefers guaranteed assigned seats
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Parking Passes</HabitTitle>
                <HabitDescription>
                  Regularly adds parking to their order
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
              </svg>
            </IconWrapper>
            Event & Division Preferences
          </h3>
          <p>What types of racing do they enjoy?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M21.04 11.13l-1.48.1c-.21 0-.4.15-.47.35l-.69 2.19-3.74-8.23c-.11-.24-.36-.4-.63-.4-.28 0-.53.17-.63.42l-2.42 6.21H9c-.41 0-.74.34-.74.74s.33.77.74.77h2.54c.27 0 .51-.17.62-.41l1.34-3.43 3.62 7.96c.11.24.36.39.63.39s.52-.16.63-.41l1.46-4.59.5-.03c.41 0 .73-.34.73-.75s-.32-.78-.73-.78zM3.72 11.13c-.37.03-.67.34-.67.74s.3.7.67.74l.5.03 1.46 4.59c.11.25.36.41.63.41s.52-.15.63-.39l3.62-7.96 1.34 3.43c.11.24.35.41.62.41H15c.41 0 .74-.34.74-.77s-.33-.74-.74-.74h-1.98l-2.42-6.21c-.1-.25-.35-.42-.63-.42-.27 0-.52.16-.63.4l-3.74 8.23-.69-2.19c-.07-.2-.26-.35-.47-.35l-1.48-.1c-.41 0-.73.37-.73.78s.32.75.73.75z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Event Types</HabitTitle>
                <HabitDescription>
                  Weekly races, Monster Trucks, Figure 8, Drag Racing, Special
                  Events
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M5 7c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4H5v3zm2.5-1.5L9 4l1.5 1.5L12 4l1.5 1.5L15 4l1.5 1.5L17 7H7V5.5zM19 10H5l1.5 10.28c.09.72.73 1.28 1.47 1.28h8.06c.74 0 1.38-.56 1.47-1.28L19 10zm-7 9l-4-4h2.5v-3h3v3H16l-4 4z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Car Divisions</HabitTitle>
                <HabitDescription>
                  Late Models, Sprint Cars, Street Stocks, Modifieds, Mini
                  Stocks
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>
      </EducationDrawer>
    </EnhancedThemeProvider>
  );
};

// Wrapper component to provide accountId from context to the GraphQL HOC
// The HOC can't use hooks directly, so we pass accountId as a prop
const CustomerDetailWithContext = (props) => {
  const {
    state: { accountId },
  } = useContext(AppContext);

  // Pass accountId and match as props so the HOC can access them in the query options
  return (
    <CustomerDetailWithQueries
      {...props}
      accountId={accountId}
      match={props.match}
    />
  );
};

// Apply all HOCs to CustomerDetail using compose
const CustomerDetailWithQueries = compose(
  GetCustomerTransaction,
  GetCustomerDetailStats,
  GetCustomerBehavioralStats,
  GetCustomerQuickStats
)(CustomerDetail);

export default CustomerDetailWithContext;
